import "../../static/css/signup.css";
import Loading from "../Loading.tsx";
import {useTranslation} from 'react-i18next';
import {Navbar} from "../../components/Navbar.tsx";
import StandAloneLogo from "../login/StandAloneLogo.tsx";
import {
    type ChangeEvent,
    type FC,
    type FormEvent,
    type  ReactNode,
    useCallback,
    useEffect,
    useState
} from "react";
import {swal_util} from "../../lib/SwalUtil.ts";
import {BlueshiftAPIConnectorError} from "../../lib/api.ts";
import LoginBlock from "./LoginBlock.tsx";
import AreasInterest from "./AreasInterest.tsx";

import {et} from "../../locales/i18n.tsx";
import {signup, SignupAPIConnectorError} from "../../lib/Signup.ts";
import {frontEndType, loginPageUrl} from "../../build_constants.ts";
import {FRONT_END_TYPE} from "../../constants.ts";
import {useNavigate} from "react-router-dom";
import {parseMultilingualMarkdown} from "../../util/MarkdownParser.ts";
import RequiredInfo from "./RequiredInfo.tsx";
import OptionalInfo from "./OptionalInfo.tsx";

interface ContentProps {
    toggleSidebar?: () => void;
    redirectPage: string;
}

const SignUpMain: FC<{ children: ReactNode }> = ({children}) => {

    if (frontEndType == FRONT_END_TYPE.XSLAB) {
        return (
            <main className="conatiner-wide">
                {children}
            </main>
        );
    }

    return (
        <main className="d-block vh-100">
            <StandAloneLogo/>
            {children}
        </main>
    );
}

const SignUp = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation('ui');
    const {i18n} = useTranslation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [data, setData] = useState<{
        success: boolean,
        areas_interest: string,
        job: string,
        terms_of_use: string,
        privacy_policy: string
    }>({
        success: false,
        areas_interest: "",
        job: "",
        terms_of_use: "",
        privacy_policy: ""
    });

    const [checked, setChecked] = useState<{ [key: string]: boolean }>({});
    const [termsHtml, setTermsHtml] = useState("");
    const [privacyHtml, setPrivacyHtml] = useState("");

    function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
        const otherCheckboxes = document.getElementsByClassName("check-agreement");
        const checked = (e.target as HTMLInputElement).checked
        Array.from(otherCheckboxes).forEach((element) => {
            const checkbox = element as HTMLInputElement;
            checkbox.checked = checked;
            setChecked((prev) => ({
                ...prev,
                [checkbox.name]: checked,
            }));
        });
    }

    function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>) {
        const {name, checked: isChecked} = event.target;
        event.currentTarget.classList.remove("is-invalid-focus");

        setChecked((prev) => {
            const newChecked: { [key: string]: boolean } = {};

            const allKeys = new Set([...Object.keys(prev), name, "ETC"]);

            if (name === "ETC" && isChecked) {
                allKeys.forEach((key) => {
                    newChecked[key] = key === "ETC";
                });
            } else {
                allKeys.forEach((key) => {
                    newChecked[key] = key === name ? isChecked : prev[key] || false;
                });
                if (isChecked) {
                    newChecked["ETC"] = false;
                }
            }

            return newChecked;
        });
    }

    const getConfig = useCallback(async () => {
        try {
            const res = await signup.getConfig();
            setData({
                success: true,
                ...res
            });
        } catch (err) {
            if (err instanceof BlueshiftAPIConnectorError) {
                if (err.cause.endsWith(".disabled")) {
                    await swal_util.show_warning(t("signUp.disabledNow"));
                    navigate(loginPageUrl, {replace: true});
                }
                throw new SignupAPIConnectorError(err, et(err.cause));
            }
            throw err;
        }
    }, [navigate, t]);

    const validateDate = (dateString: string) => {
        const cleanDate = dateString.replace(/[^\d]/g, '');

        if (cleanDate.length !== 8) return false;

        const year = parseInt(cleanDate.substring(0, 4));
        const month = parseInt(cleanDate.substring(4, 6));
        const day = parseInt(cleanDate.substring(6, 8));

        const date = new Date(year, month - 1, day);

        return date.getFullYear() === year &&
            date.getMonth() + 1 === month &&
            date.getDate() === day &&
            date <= new Date();
    };

    async function onSubmitSignUpForm(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const frmData = new FormData(e.currentTarget);
        const id = frmData.get("id") as string;
        const email = frmData.get("email") as string;
        const name = frmData.get("name") as string;
        const p_num = frmData.get("p_num") as string;
        const addr = frmData.get("addr") as string;
        const addr_detail = frmData.get("addr_detail") as string;
        const office = frmData.get("office") as string;
        const birthday = frmData.get("birthday") as string;
        const selected_job = frmData.get("selected_job") as string;
        const sign_up_path = frmData.get("sign_up_path") as string;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const pNumRegex = /^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/
        const birthRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

        let interests: string[];
        let job: string;

        if (selected_job === "ETC") {
            job = frmData.get("job") as string;
        } else {
            job = selected_job ?? "";
        }

        if (checked["ETC"]) {
            const interestsData = frmData.get("interests") as string;
            interests = interestsData
                .split(",")
                .map((item) => item.trim())
                .filter((item) => item !== "");
        } else {
            interests = Object.keys(checked).filter(
                (key) => checked[key] && !["terms_of_use", "privacy_policy"].includes(key)
            );
        }
        const form = e.currentTarget;

        const focusAndError = async (fieldName: string, message: string) => {
            const target = form.elements.namedItem(fieldName) as HTMLElement | null;
            target?.classList.add("is-invalid-focus");
            target?.focus();
            await swal_util.show_error(message);
            return false;
        };

        if (!id.trim()) return await focusAndError("id", t("signUp.error.enterId"));
        if (!email.trim()) return await focusAndError("email", t("signUp.error.enterEmail"));
        if (!name.trim()) return await focusAndError("name", t("signUp.error.enterName"));
        if (!p_num.trim()) return await focusAndError("p_num", t("signUp.error.enterPhone"));
        if (!pNumRegex.test(p_num)) return await focusAndError("p_num", t("signUp.error.invalidPhone"));
        if (!emailRegex.test(email)) return await focusAndError("email", t("signUp.error.invalidEmail"));
        if (!checked["terms_of_use"]) return await focusAndError("terms_of_use", t("signUp.error.acceptTerms"));
        if (!checked["privacy_policy"]) return await focusAndError("privacy_policy", t("signUp.error.acceptTerms"));
        if (birthday && !birthRegex.test(birthday) && !validateDate(birthday))
            return await focusAndError("birthday", t("signUp.error.invalidBirth"));

        try {
            const req = {
                id: id,
                email: email,
                name: name,
                telephone: p_num,
                job: job,
                office: office.trim(),
                address: `${addr} ${addr_detail}`,
                interests: interests,
                birthday: birthday,
                sign_up_path: sign_up_path.trim(),
            }
            setLoading(true);
            await signup.signup(req);
        } catch (e) {
            if (e instanceof SignupAPIConnectorError) {
                const errorMessage = String(e.message);

                if (errorMessage.includes("already exists")) {
                    await swal_util.show_error(t("signUp.emailAlreadyExists"));
                } else {
                    await swal_util.show_error(errorMessage);
                }
            } else {
                await swal_util.show_error(t("signUp.failed"));
            }

            setLoading(false);

            throw e;
        }

        setLoading(false);
        await swal_util.show_info(t("signUp.pleaseWaitAMomentAndCheckYourEmail"));
        navigate(loginPageUrl, {replace: true});
    }

    useEffect(() => {
        getConfig().catch(() => swal_util.show_error("서버 점검중!!!!!!").then(() => navigate(-1))).finally();
    }, [getConfig, navigate]);

    useEffect(() => {
        async function renderMarkdown() {
            setTermsHtml(await parseMultilingualMarkdown(data.terms_of_use || "", i18n.language));
            setPrivacyHtml(await parseMultilingualMarkdown(data.privacy_policy || "", i18n.language));
        }

        renderMarkdown().finally();
    }, [data.terms_of_use, data.privacy_policy, i18n.language]);

    return (
        <>
            {loading && <Loading text={t("signUp.membershipRegistrationInProgress")}/>}
            {
                data.success && <SignUpMain>
                    <Navbar toggleSidebar={toggleSidebar}/>

                    <article className="container-wide" id="wrapper">
                        <div className="content section core-value">
                            <div className="row row-heading align-items-end justify-content-between">
                                <div className="col-sm-auto w-100">
                                    <div className="section-heading text-sm-left is-visible">
                                        <h4>{t("signUp.welcomeFirstTime")}</h4>
                                        <h2 className="">{t("signUp.signUp")}</h2>
                                    </div>
                                    <span className="d-flex justify-content-end align-items-center">
                                        <span className="required-custom"></span>{t("signUp.requiredInput")}</span>
                                    <div className="division-line"></div>
                                </div>
                            </div>
                            <div className="form-box">
                                <form className="custom-validation" id="form_signup" action="#"
                                      onSubmit={onSubmitSignUpForm}>
                                    <div className="row row-cols-1 row-cols-md-2 mb-4">
                                        <RequiredInfo/>
                                        <OptionalInfo jobData={data.job}/>
                                    </div>
                                    <AreasInterest areasInterestData={data.areas_interest} checked={checked}
                                                   handleCheckboxChange={handleCheckboxChange}/>

                                    <div className="card rounded-4 p-4">
                                        <h5 className="pb-4 subheadings">{t("signUp.acceptOfTerms")}</h5>

                                        <div id="text_box" dangerouslySetInnerHTML={{__html: termsHtml}}>
                                        </div>
                                        <div className="div_input col-6 pb-4 ms-2">
                                            <input type="checkbox" className="form-check-input check-agreement"
                                                   name="terms_of_use"
                                                   checked={checked["terms_of_use"] ?? false}
                                                   onChange={handleCheckboxChange}/>
                                            <h6 className="text-skyblue required-custom">{t("signUp.termsOfUseAccept")}</h6>
                                        </div>

                                        <div id="text_box" dangerouslySetInnerHTML={{__html: privacyHtml}}></div>
                                        <div className="div_input col-6 ms-2">
                                            <input type="checkbox" className="form-check-input check-agreement"
                                                   name="privacy_policy"
                                                   checked={checked["privacy_policy"] ?? false}
                                                   onChange={handleCheckboxChange}/>
                                            <h6 className="text-skyblue required-custom">{t("signUp.privacyPolicyAccept")}</h6>
                                        </div>

                                        <div className="div_input col-6 ms-2 pt-0">
                                            <input type="checkbox" className="form-check-input" name="full_consent"
                                                   checked={(checked["terms_of_use"] && checked["privacy_policy"]) ?? false}
                                                   onChange={handleSelectAll}/>
                                            <h6 className="text-skyblue">{t("signUp.fullConsent")}</h6>
                                        </div>
                                    </div>

                                    <div className="mt-4 d-grid">
                                        <button className="button_blue"
                                                type="submit">{t("signUp.signUp")}</button>
                                    </div>

                                    <LoginBlock/>
                                </form>
                            </div>
                        </div>
                    </article>
                </SignUpMain>
            }
            {
                !data.success && <SignUpMain>
                    <Navbar toggleSidebar={toggleSidebar}/>
                </SignUpMain>
            }
        </>
    );
}

export default SignUp;